import React, { useState } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Typography, Collapse, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import HelpIcon from '@mui/icons-material/Help';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PersonIcon from '@mui/icons-material/Person';
import CommentIcon from '@mui/icons-material/Comment';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import HistoryIcon from '@mui/icons-material/History';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { AppBar } from './';
import { LogoutButton, PasswordChangeDialog } from './auth';
import { FeedbackModal } from './feedback/FeedbackModal';
import { useUserRoles } from '../hooks/useUserRoles';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Layout component that provides the common app structure (AppBar, Drawer, etc.)
 * Used to wrap page content and avoid code duplication in routing
 */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useUserRoles();
  const { isAuthenticated, user } = useAuth();

  const handleMenuClick = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  // Load notification preference when profile menu opens
  React.useEffect(() => {
    if (profileMenuOpen && !notificationsLoaded && isAuthenticated) {
      apiClient.get<{ enabled: boolean }>('/api/user/notification-preference')
        .then((data) => {
          setNotificationsEnabled(data.enabled);
          setNotificationsLoaded(true);
        })
        .catch(() => {
          setNotificationsLoaded(true);
        });
    }
  }, [profileMenuOpen, notificationsLoaded, isAuthenticated]);

  const handleToggleNotifications = async () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    try {
      await apiClient.put('/api/user/notification-preference', { enabled: newValue });
    } catch {
      setNotificationsEnabled(!newValue);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      bgcolor: 'background.default',
      color: 'text.primary',
      overflowX: 'hidden'
    }}>
      {/* App Bar */}
      <AppBar onMenuClick={handleMenuClick} />
      
      {/* Drawer Menu */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
            color: 'text.primary',
            borderLeft: '1px solid rgba(255, 255, 255, 0.12)'
          }
        }}
      >
        <Box
          sx={{ width: 280, display: 'flex', flexDirection: 'column', height: '100%' }}
          role="presentation"
        >
          <Box sx={{ 
            py: 3, 
            px: 2, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)'
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #4dd0e1 30%, #80deea 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Menu
            </Typography>
          </Box>

          {/* User Profile Section - only show when authenticated */}
          {import.meta.env.VITE_AUTH_ENABLED === 'true' && isAuthenticated && (
            <>
              <List sx={{ pt: 2 }}>
                <ListItem 
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)} 
                  sx={{ 
                    cursor: 'pointer',
                    borderRadius: 1,
                    mx: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(77, 208, 225, 0.08)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: '#4dd0e1', minWidth: 40 }}>
                    <Avatar 
                      src={user?.picture} 
                      alt={user?.name || user?.email}
                      sx={{ width: 28, height: 28 }}
                    >
                      {!user?.picture && <PersonIcon sx={{ width: 20, height: 20 }} />}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary={user?.email || 'Profile'}
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      noWrap: true,
                      sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '150px'
                      }
                    }}
                  />
                  {profileMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                
                <Collapse in={profileMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem 
                      onClick={() => {
                        setPasswordDialogOpen(true);
                        setMenuOpen(false);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(77, 208, 225, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#4dd0e1' }}>
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Change Password"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    <ListItem
                      onClick={handleToggleNotifications}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(77, 208, 225, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: notificationsEnabled ? '#4dd0e1' : 'text.secondary' }}>
                        {notificationsEnabled ? <NotificationsIcon /> : <NotificationsOffIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={notificationsEnabled ? 'Notifications On' : 'Notifications Off'}
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                  </List>
                </Collapse>
              </List>
              
              <Divider sx={{ my: 1.5, mx: 2, opacity: 0.6 }} />
            </>
          )}
          
          <List sx={{ pt: 2, flex: 1, overflowY: 'auto' }}>
            <ListItem
              onClick={() => handleNavigation('/map')}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                mx: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(77, 208, 225, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#4dd0e1' }}>
                <TravelExploreIcon />
              </ListItemIcon>
              <ListItemText
                primary="Forecast Map"
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
              />
            </ListItem>

            <ListItem
              onClick={() => handleNavigation('/')}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                mx: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(77, 208, 225, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#4dd0e1' }}>
                <ViewTimelineIcon />
              </ListItemIcon>
              <ListItemText
                primary="Forecast Timelines"
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
              />
            </ListItem>

            <Divider sx={{ my: 1.5, mx: 2, opacity: 0.6 }} />

            <ListItem
              onClick={() => handleNavigation('/help')} 
              sx={{ 
                cursor: 'pointer',
                borderRadius: 1,
                mx: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(77, 208, 225, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#4dd0e1' }}>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Help & Documentation" 
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
              />
            </ListItem>
            
            <Divider sx={{ my: 1.5, mx: 2, opacity: 0.6 }} />
            
            <ListItem 
              onClick={() => {
                setFeedbackModalOpen(true);
                setMenuOpen(false);
              }} 
              sx={{ 
                cursor: 'pointer',
                borderRadius: 1,
                mx: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(77, 208, 225, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#4dd0e1' }}>
                <FeedbackOutlinedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Feedback" 
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
              />
            </ListItem>
            
            {/* Admin menu item - only show when user has admin privileges */}
            {import.meta.env.VITE_AUTH_ENABLED === 'true' && isAdmin() && (
              <>
                <Divider sx={{ my: 1.5, mx: 2, opacity: 0.6 }} />
                
                <ListItem 
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)} 
                  data-testid="admin-menu-toggle"
                  sx={{ 
                    cursor: 'pointer',
                    borderRadius: 1,
                    mx: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 152, 0, 0.08)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: '#ff9800' }}>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Admin" 
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                  />
                  {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                
                <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem 
                      onClick={() => handleNavigation('/admin/users')} 
                      sx={{ 
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <PeopleIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Users" 
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    
                    {isSuperAdmin() && (
                    <ListItem
                      onClick={() => handleNavigation('/admin/organizations')}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <BusinessIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Organisations"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    )}

                    {isSuperAdmin() && (
                    <ListItem
                      onClick={() => handleNavigation('/admin/location-groups')}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <WorkspacesIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Location Groups"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    )}

                    {isSuperAdmin() && (
                    <ListItem
                      onClick={() => handleNavigation('/admin/audit-log')}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <HistoryIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Audit Log"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    )}

                    {isSuperAdmin() && (
                    <ListItem
                      onClick={() => handleNavigation('/admin/feature-flags')}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <ToggleOnIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Feature Flags"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    )}

                    {isSuperAdmin() && (
                    <ListItem
                      onClick={() => handleNavigation('/admin/feedback')}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mx: 1,
                        pl: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#ff9800' }}>
                        <CommentIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Feedback"
                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                      />
                    </ListItem>
                    )}
                  </List>
                </Collapse>
              </>
            )}
          </List>
          
          {/* Sign Out Button at bottom of menu */}
          <Box sx={{ p: 2 }}>
            <LogoutButton fullWidth />
          </Box>
        </Box>
      </Drawer>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          height: 'auto',
          overflowY: 'visible',
          // Only apply scroll container on tablets+ (excludes mobile landscape)
          '@media (min-width: 600px) and (min-height: 500px)': {
            height: 'calc(100vh - 64px)', // 64px is AppBar height at sm+ breakpoint
            overflowY: 'auto'
          }
        }}
      >
        {children}
      </Box>
      
      {/* Password Change Dialog */}
      <PasswordChangeDialog 
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
      
      {/* Feedback Modal */}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </Box>
  );
};